/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
	useState,
	Fragment,
	useEffect,
	useRef,
	useCallback,
	useMemo,
	memo,
	type Dispatch,
	type SetStateAction,
} from 'react'

import { ChevronDown20Regular, LockClosed16Filled } from '@fluentui/react-icons'
import { Combobox, Transition } from '@headlessui/react'
import type { FilterStateProps } from '@hooks/useQuestion'
import { styled } from '@styles/theme'
import { useVirtualizer } from '@tanstack/react-virtual'

import { CheckBox } from '../CheckBox'
import { autoCompleteStyles } from './styles'

const {
	ComboboxButton,
	ComboboxButtonDisabled,
	ComboboxOptions,
	ComboboxOption,
	comboboxInput,
	ContainerComboboxInput,
	ScrollOptions,
	NumberSelected,
} = autoCompleteStyles

const Error = styled('span', autoCompleteStyles.error)

type StitchesProps = React.ComponentProps<typeof ComboboxButton>
type OptionsProps = { name: string; value: number | string }

interface SelectProps extends StitchesProps {
	error?: string
	options?: Array<OptionsProps>
	disabled?: boolean
	placeholder?: string
	selectedText?: string
	onChange: Dispatch<SetStateAction<FilterStateProps>> | undefined
	name: string
	selectedValue?: any
	onSearch?: (term: string) => void
}

const useDebounce = <T,>(value: T, delay: number): T => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const VirtualizedOption = memo(({ item }: { item: OptionsProps }) => (
	<Combobox.Option className={ComboboxOption()} value={item}>
		{({ selected }) => <CheckBox label={item.name} checked={selected} />}
	</Combobox.Option>
))

const VirtualizedList = memo(({ items }: { items: Array<OptionsProps> }) => {
	const parentRef = useRef<HTMLDivElement>(null)

	const virtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 50,
		overscan: 10,
	})

	const rowItems = virtualizer.getVirtualItems()

	return (
		<ScrollOptions ref={parentRef}>
			<div
				style={{
					height: `${virtualizer.getTotalSize()}px`,
					width: '100%',
					position: 'relative',
				}}
			>
				<div
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						transform: `translateY(${rowItems[0]?.start || 0}px)`,
					}}
				>
					{rowItems.map((virtualRow) => {
						const item = items[virtualRow.index]

						return <VirtualizedOption key={item.value} item={item} />
					})}
				</div>
			</div>
		</ScrollOptions>
	)
})

const removeAccents = (str: string) => {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\s+/g, '')
}

export const AutocompleteBase = memo(
	({
		error,
		options = [],
		disabled,
		placeholder = 'Selecione',
		selectedText = 'Disciplina...',
		onChange,
		name,
		selectedValue = [],
		onSearch,
		...props
	}: SelectProps) => {
		const [query, setQuery] = useState('')
		const debouncedQuery = useDebounce(query, 300)

		const filteredOptions = useMemo(() => {
			if (!debouncedQuery) return options

			const cleanQuery = removeAccents(debouncedQuery)

			return options.filter((item) =>
				removeAccents(item.name).includes(cleanQuery)
			)
		}, [options, debouncedQuery])

		const handleChange = useCallback(
			(items: OptionsProps[]) => {
				if (onChange) {
					onChange((prevState: any) => ({
						...prevState,
						[name]: items,
					}))
				}
			},
			[onChange, name]
		)

		useEffect(() => {
			onSearch?.(query)
		}, [query, onSearch])

		return (
			<div style={{ position: 'relative', width: '100%' }}>
				<Combobox
					value={selectedValue}
					onChange={handleChange}
					disabled={disabled}
					multiple
					{...props}
				>
					{disabled ? (
						<ComboboxButtonDisabled>
							{selectedValue.length > 0 ? selectedText : placeholder}
							<div style={{ display: 'flex', gap: '16px' }}>
								{selectedValue.length > 0 && (
									<NumberSelected>{selectedValue.length}</NumberSelected>
								)}
								<div style={{ display: 'flex', gap: '8px' }}>
									<LockClosed16Filled />
									<ChevronDown20Regular />
								</div>
							</div>
						</ComboboxButtonDisabled>
					) : (
						<ComboboxButton>
							{selectedValue.length > 0 ? selectedText : placeholder}
							<div style={{ display: 'flex', gap: '16px' }}>
								{selectedValue.length > 0 && (
									<NumberSelected>{selectedValue.length}</NumberSelected>
								)}
								<ChevronDown20Regular />
							</div>
						</ComboboxButton>
					)}

					<Transition
						as={Fragment}
						leave='transition ease-in duration-100'
						leaveFrom='opacity-100'
						leaveTo='opacity-0'
						afterLeave={() => setQuery('')}
					>
						<ComboboxOptions>
							<ContainerComboboxInput>
								<Combobox.Input
									placeholder='Pesquisar'
									className={comboboxInput()}
									onChange={(e) => setQuery(e.target.value)}
									value={query}
								/>
							</ContainerComboboxInput>

							{filteredOptions.length === 0 && query ? (
								<div style={{ padding: '10px', textAlign: 'center' }}>
									Nenhum resultado encontrado
								</div>
							) : (
								<VirtualizedList items={filteredOptions} />
							)}
						</ComboboxOptions>
					</Transition>
				</Combobox>

				{error && <Error>{error}</Error>}
			</div>
		)
	}
)
